/** UI + envio desktop desligados até revisão; reativar com true */
export const WHATSAPP_DESKTOP_NOTIFY_UI_ENABLED = false

const SOUND_URL = '/sounds/whatsapp-incoming.mp3'
const NOTIFICATION_ICON = '/discord-icon.svg'
const PREVIEW_MAX_LEN = 80

let audioInstance = null
let audioUnlocked = false
let onNotificationClickHandler = null
/** @type {null | (() => object | null)} */
let kanbanNotifyContextProvider = null
let whatsAppNotifyListenerRegistered = false

export function getWhatsAppNotifyStorageKeys(userId) {
  const id = userId != null ? String(userId) : 'anonymous'
  return {
    sound: `backlog-whatsapp-notify-sound:${id}`,
    desktop: `backlog-whatsapp-notify-desktop:${id}`
  }
}

export function loadWhatsAppNotifyPrefs(userId) {
  const keys = getWhatsAppNotifyStorageKeys(userId)
  return {
    sound: localStorage.getItem(keys.sound) === 'true',
    desktop: localStorage.getItem(keys.desktop) === 'true'
  }
}

export function saveWhatsAppNotifyPref(userId, key, value) {
  const keys = getWhatsAppNotifyStorageKeys(userId)
  const storageKey = key === 'sound' ? keys.sound : keys.desktop
  localStorage.setItem(storageKey, value ? 'true' : 'false')
}

export function parseIncomingPayload(payload) {
  if (payload == null) return null
  if (typeof payload === 'string') {
    try {
      return JSON.parse(payload)
    } catch {
      return null
    }
  }
  return payload
}

function isCrmIncoming(leadId, workspaceTipo) {
  return workspaceTipo === 'CRM' && leadId != null && leadId !== ''
}

/** Modal aberto no mesmo lead (estado Vue — pode permanecer true com aba em segundo plano). */
export function isViewingSameLeadChat({ showLeadDetailModal, selectedLeadId, leadId }) {
  return Boolean(
    showLeadDetailModal
    && selectedLeadId != null
    && leadId != null
    && Number(selectedLeadId) === Number(leadId)
  )
}

/** Som: não tocar se o usuário está no chat desse lead com a aba em foco. */
export function shouldPlayIncomingSound({ leadId, workspaceTipo, showLeadDetailModal, selectedLeadId }) {
  if (!isCrmIncoming(leadId, workspaceTipo)) return false
  if (isViewingSameLeadChat({ showLeadDetailModal, selectedLeadId, leadId }) && !isPageInBackground()) {
    return false
  }
  return true
}

/** Desktop: mesma regra do som — exceto chat do mesmo lead com aba em foco. */
export function shouldShowDesktopNotification(notifyCtx) {
  const { leadId, workspaceTipo, prefs, showLeadDetailModal, selectedLeadId } = notifyCtx || {}
  if (!isCrmIncoming(leadId, workspaceTipo)) return false
  if (!prefs?.desktop) return false
  if (!canShowDesktopNotifications()) return false
  if (isViewingSameLeadChat({ showLeadDetailModal, selectedLeadId, leadId }) && !isPageInBackground()) {
    return false
  }
  return true
}

/** Motivo de não exibir desktop (null = pode exibir). */
export function getDesktopNotificationSkipReason(notifyCtx) {
  const { leadId, workspaceTipo, prefs, showLeadDetailModal, selectedLeadId } = notifyCtx || {}
  if (!isCrmIncoming(leadId, workspaceTipo)) return 'not_crm_incoming'
  if (!prefs?.desktop) return 'desktop_toggle_off'
  if (!canShowDesktopNotifications()) return 'permission_not_granted'
  if (isViewingSameLeadChat({ showLeadDetailModal, selectedLeadId, leadId }) && !isPageInBackground()) {
    return 'viewing_same_lead_focused'
  }
  return null
}

/** 'granted' | 'denied' | 'default' | 'unsupported' */
export function getNotificationPermission() {
  if (typeof Notification === 'undefined') return 'unsupported'
  const p = Notification.permission
  if (p === 'granted' || p === 'denied' || p === 'default') return p
  return 'unsupported'
}

export function formatNotificationPermissionHint(t) {
  const perm = getNotificationPermission()
  const keyMap = {
    granted: 'permissionStatusGranted',
    default: 'permissionStatusDefault',
    denied: 'permissionStatusDenied',
    unsupported: 'permissionUnsupportedText'
  }
  const key = keyMap[perm] || 'permissionStatusDefault'
  const origin = typeof window !== 'undefined' ? window.location.origin : ''
  return t(`kanban.page.toggles.whatsappNotify.${key}`, { origin })
}

/**
 * Registra contexto do Kanban (modal aberto, leads, workspace). Chamado pelo KanbanPage.
 */
export function registerWhatsAppKanbanNotifyProvider(provider) {
  kanbanNotifyContextProvider = typeof provider === 'function' ? provider : null
}

function buildNotificationOptions({ title, body, tag }) {
  const opts = {
    body,
    icon: resolveNotificationIcon(),
    tag: tag || `whatsapp-${Date.now()}`,
    silent: false
  }
  // Firefox/Chrome: renotify ajuda quando a mesma tag seria substituída sem alerta visível
  try {
    opts.renotify = true
  } catch {
    /* ignore */
  }
  return opts
}

export function showTestDesktopNotification(t) {
  if (!canShowDesktopNotifications()) {
    console.warn('[WhatsAppNotify] Teste omitido:', {
      permission: getNotificationPermission(),
      origin: typeof window !== 'undefined' ? window.location.origin : ''
    })
    return false
  }
  try {
    const n = new Notification(
      t('kanban.page.toggles.whatsappNotify.testTitle'),
      buildNotificationOptions({
        body: t('kanban.page.toggles.whatsappNotify.testBody'),
        tag: `whatsapp-notify-test-${Date.now()}`
      })
    )
    n.onclick = () => {
      window.focus()
      n.close()
    }
    return true
  } catch (e) {
    console.warn('[WhatsAppNotify] Falha na notificação de teste:', e, {
      permission: getNotificationPermission(),
      origin: typeof window !== 'undefined' ? window.location.origin : ''
    })
    return false
  }
}

function getAudio() {
  if (typeof Audio === 'undefined') return null
  if (!audioInstance) {
    audioInstance = new Audio(SOUND_URL)
    audioInstance.preload = 'auto'
    audioInstance.volume = 0.45
  }
  return audioInstance
}

export function unlockAudioOnUserGesture() {
  const audio = getAudio()
  if (!audio || audioUnlocked) return
  const prevVolume = audio.volume
  audio.volume = 0
  const p = audio.play()
  if (p && typeof p.then === 'function') {
    p.then(() => {
      audio.pause()
      audio.currentTime = 0
      audio.volume = prevVolume
      audioUnlocked = true
    }).catch(() => {
      audio.volume = prevVolume
    })
  }
}

export function playIncomingSound() {
  const audio = getAudio()
  if (!audio) return
  audio.currentTime = 0
  audio.volume = 0.45
  const p = audio.play()
  if (p && typeof p.catch === 'function') {
    p.catch(() => {})
  }
}

export function setWhatsAppNotificationClickHandler(handler) {
  onNotificationClickHandler = typeof handler === 'function' ? handler : null
}

export function buildMessagePreview(message, messageType, t) {
  const type = String(messageType || 'TEXT').toUpperCase()
  if (type === 'TEXT') {
    const text = String(message || '').trim()
    if (!text) return t('kanban.page.toggles.whatsappNotify.previewText')
    return text.length > PREVIEW_MAX_LEN ? `${text.slice(0, PREVIEW_MAX_LEN)}…` : text
  }
  const keyMap = {
    IMAGE: 'previewImage',
    AUDIO: 'previewAudio',
    VIDEO: 'previewVideo',
    DOCUMENT: 'previewDocument'
  }
  const key = keyMap[type] || 'previewOther'
  return t(`kanban.page.toggles.whatsappNotify.${key}`)
}

/** Aba oculta ou janela do navegador sem foco (ex.: outro app em primeiro plano no Windows). */
export function isPageInBackground() {
  if (typeof document === 'undefined') return false
  if (document.hidden) return true
  if (typeof document.hasFocus === 'function' && !document.hasFocus()) return true
  return false
}

export function canShowDesktopNotifications() {
  return typeof Notification !== 'undefined' && Notification.permission === 'granted'
}

function resolveNotificationIcon() {
  try {
    if (typeof window !== 'undefined' && window.location?.origin) {
      return new URL(NOTIFICATION_ICON, window.location.origin).href
    }
  } catch {
    // fallback path relativo
  }
  return NOTIFICATION_ICON
}

export function showDesktopNotification({ leadId, leadName, body, t }) {
  if (!canShowDesktopNotifications()) return false

  const title = leadName
    ? t('kanban.page.toggles.whatsappNotify.notificationTitle', { name: leadName })
    : t('kanban.page.toggles.whatsappNotify.notificationTitleFallback')

  try {
    const n = new Notification(
      title,
      buildNotificationOptions({
        body,
        tag: `whatsapp-lead-${leadId}-${Date.now()}`
      })
    )
    n.onshow = () => {
      console.log('[WhatsAppNotify] Notification onshow', { leadId })
    }
    n.onerror = (err) => {
      console.warn('[WhatsAppNotify] Notification onerror', { leadId, err })
    }
    n.onclick = () => {
      window.focus()
      n.close()
      if (onNotificationClickHandler) {
        onNotificationClickHandler(leadId)
      }
    }
    return true
  } catch (e) {
    console.warn('[WhatsAppNotify] Falha ao exibir notificação desktop:', e)
    return false
  }
}

/**
 * Chamar no clique do usuário, sem await antes — senão o navegador não exibe o prompt.
 * Retorna Promise com 'granted' | 'denied' | 'default' | 'unsupported'.
 */
export function requestDesktopNotificationPermissionFromGesture() {
  if (typeof Notification === 'undefined') {
    return Promise.resolve('unsupported')
  }
  const current = Notification.permission
  if (current === 'granted' || current === 'denied') {
    return Promise.resolve(current)
  }
  try {
    const result = Notification.requestPermission()
    if (result && typeof result.then === 'function') {
      return result
    }
    return Promise.resolve(result)
  } catch {
    return Promise.resolve('denied')
  }
}

export async function requestDesktopNotificationPermission() {
  return requestDesktopNotificationPermissionFromGesture()
}

function handleGlobalWhatsAppMessageReceived(evt) {
  const provider = kanbanNotifyContextProvider
  if (!provider) return

  const kanbanCtx = provider()
  if (!kanbanCtx || kanbanCtx.workspaceTipo !== 'CRM') return

  const userId = kanbanCtx.userId
  const storedPrefs = loadWhatsAppNotifyPrefs(userId)
  const prefs = {
    sound: storedPrefs.sound,
    desktop: WHATSAPP_DESKTOP_NOTIFY_UI_ENABLED && storedPrefs.desktop
  }

  notifyWhatsAppIncoming({
    payload: evt?.detail,
    leads: kanbanCtx.leads,
    workspaceTipo: kanbanCtx.workspaceTipo,
    showLeadDetailModal: kanbanCtx.showLeadDetailModal,
    selectedLeadId: kanbanCtx.selectedLeadId,
    prefs,
    t: kanbanCtx.t
  })
}

/** Listener global no CustomEvent (mesmo evento do WebSocket). Uma vez por aba. */
export function ensureWhatsAppNotifyListener() {
  if (whatsAppNotifyListenerRegistered || typeof window === 'undefined') return
  whatsAppNotifyListenerRegistered = true
  window.addEventListener('whatsapp.message.received', handleGlobalWhatsAppMessageReceived)
}

export function notifyWhatsAppIncoming(ctx) {
  const {
    payload,
    leads,
    workspaceTipo,
    showLeadDetailModal,
    selectedLeadId,
    prefs,
    t
  } = ctx || {}

  const msg = parseIncomingPayload(payload)
  if (!msg) return

  const direction = String(msg.direction || '').toUpperCase()
  if (direction !== 'INCOMING') return

  const leadId = msg.leadId
  if (!isCrmIncoming(leadId, workspaceTipo)) return

  const lead = Array.isArray(leads) ? leads.find((l) => Number(l.id) === Number(leadId)) : null
  const leadName = lead?.nome || null
  const preview = buildMessagePreview(msg.message, msg.messageType, t)

  const notifyCtx = { leadId, workspaceTipo, showLeadDetailModal, selectedLeadId, prefs }
  const playSound = prefs?.sound && shouldPlayIncomingSound(notifyCtx)

  if (playSound) {
    playIncomingSound()
  }

  if (prefs?.desktop) {
    const skipReason = getDesktopNotificationSkipReason(notifyCtx)
    if (skipReason) {
      console.log('[WhatsAppNotify] Desktop omitida:', skipReason, {
        leadId,
        permission: typeof Notification !== 'undefined' ? Notification.permission : 'unsupported',
        hidden: typeof document !== 'undefined' ? document.hidden : null,
        hasFocus: typeof document !== 'undefined' && typeof document.hasFocus === 'function'
          ? document.hasFocus()
          : null,
        background: isPageInBackground(),
        modalMesmoLead: isViewingSameLeadChat(notifyCtx)
      })
    } else {
      const shown = showDesktopNotification({ leadId, leadName, body: preview, t })
      console.log('[WhatsAppNotify] Desktop', shown ? 'shown' : 'failed', {
        leadId,
        hidden: typeof document !== 'undefined' ? document.hidden : null,
        background: isPageInBackground()
      })
    }
  }
}
